import { observer } from "mobx-react";
import { FC, Fragment, ReactNode } from "react";
import { Alert } from "@/components-v2/Alert";
import { Button, IconButton, LinkButton } from "@/components-v2/Button";
import {
  GeneralMeetingCustomPanel,
  GeneralMeetingMinutesForm,
  GeneralMeetingVotingModel,
  getGeneralMeetingCustomAgendaItems,
  getGeneralMeetingCustomPanels,
  NewSigner,
  NewSignerForm,
  SelectedSigners,
  SignerOption,
  trimWhitespace,
  withCreateNewOption,
} from "./generalMeetingMinutesDomain";
import { DetailsPanel, getDecisionDetailsPanels, ProjectContext } from "@/dbot/common/commonDomain";
import {
  DecisionTextsView,
  DetailsSummaryList,
  DocumentLanguageSelector,
  DocumentSigningStatusLine,
  ReopenDocumentButton,
  TextQuestion,
  YesNoQuestion,
} from "@/dbot/common/commonComponents";
import {
  PlaceOfGeneralMeeting,
  TimeOfGeneralMeeting,
  VotingOutcomeSettings,
  VotingTypeSettings,
} from "@/dbot/common/commonGeneralMeetingComponents";
import {
  annualAccountWarning,
  GeneralMeetingForm,
  GeneralMeetingVoting,
  IndividualVote,
  isCirculationInvalid,
  isVoted,
  mapRoleToCompanyRoleIdWithName,
} from "@/dbot/common/commonGeneralMeetingDomain";
import { Checkbox } from "@/components-v2/Checkbox";
import { Box, VBox } from "@/components-v2/Box";
import { RadioButtonGroup } from "@/components-v2/RadioButton";
import { compact, identity, sortBy } from "lodash";
import { Dialog } from "@/components-v2/Dialog";
import { MTextInput } from "@/components-v2/TextInput";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { Autocomplete, AutocompleteProps } from "@/components-v2/Autocomplete/Autocomplete";
import { defaultFilterFn } from "@/components-v2/Autocomplete/autocompleteHelpers";
import { Table } from "@/components-v2/Table";
import { AgendaActions } from "@/dbot/common/commonAgendaComponents";
import { AttachmentsPanel } from "@/components-v2/AttachmentsPanel";
import { when } from "@/utils/functions";
import { isProductionEnvironment } from "@/utils/environments";
import { LiveVotingResults } from "@/meetings/generalMeeting/electronicGeneralMeetingDomain";
import { newUuid } from "@/utils/uuid";
import { isPending } from "@/common/common";
import { useLocaleFormat, useTranslation } from "@/stores/RootStoreContext";
import { DecisionType } from "@/common/decisions";
import { RadioFieldQuestion } from "@/common/commonComponents";
import { findShareTypeName } from "@/common/share";
import { LocalizedTextEditor } from "@/text/LocalizedTextEditor";
import {
  prepDocumentAttachmentsPanel,
  prepUploadDocumentAttachmentsButton,
} from "@/dbot/attachments";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { ShareholderDTO } from "@/types/Shareholder";
import { Icon } from "@/components-v2/Icon";

type VotesProps = {
  voting: GeneralMeetingVotingModel;
  data?: LiveVotingResults; // used if voting is Live
  onChange: (voting: GeneralMeetingVoting) => void;
  onLinkClick: () => void;
  isProjectLocked?: boolean;
  isElectronicGeneralMeeting: boolean;
};

const AutocompleteSigner = (
  props: Omit<AutocompleteProps<SignerOption>, "labelFn" | "idFn" | "filterFn">,
) => {
  const { t } = useTranslation();
  return (
    <Autocomplete
      {...props}
      filterFn={({ items, labelFn }, inputValue) => {
        const trimmed = trimWhitespace(inputValue);
        return withCreateNewOption(defaultFilterFn({ items, labelFn }, trimmed), trimmed);
      }}
      labelFn={(option) =>
        option?.companyroleId === undefined && !option?.inputValue
          ? t("decisions.autocomplete-register-signer-empty-input")
          : option.companyroleId === undefined
            ? t("decisions.autocomplete-register-signer", { name: option.inputValue })
            : option.name
      }
      renderInputValue={(option) =>
        (option?.companyroleId === undefined ? option?.inputValue : option.name) ?? ""
      }
      iconFn={(option) => (option?.companyroleId ? undefined : "plus")}
      idFn={(option) => String(option?.companyroleId ?? "")}
    />
  );
};

const ShareholdersAttendingDialog: FC<{ form: GeneralMeetingMinutesForm }> = observer(
  ({ form }) => {
    const { t, lang } = useTranslation();
    const { formatNumber, formatPercent } = useLocaleFormat();
    const save = () => {
      form.hideAttendingDialog();
      form.saveParticipation();
    };
    const columns: TableColumn<ShareholderDTO>[] = [
      {
        name: "participatingShareholders",
        label: t("documents.attendingShareholders.present"),
        align: "left",
        getValue: (row) => (
          <Checkbox
            label={
              <Box>
                <Icon name={row.isCompany ? "buildings" : "user-circle"} />
                <span>{row.name}</span>
              </Box>
            }
            disabled={form.isLocked}
            checked={form.isShareholderPresent(row.id)}
            onChange={() => form.toggleShareholderPresent(row.id)}
          />
        ),
        getSortValue: (row) => row.name,
        getFooterValue: () => (
          <>
            {form.numberOfPresentShareholders === 1
              ? t("documents.attendingShareholders.total-shareholders.singular")
              : t("documents.attendingShareholders.total-shareholders.plural", {
                  shareholders: form.numberOfPresentShareholders,
                })}
          </>
        ),
      },
      {
        name: "birthOrOrg",
        label: t("documents.attendingShareholders.birthOrOrg"),
        align: "left",
        getValue: (row) => row.birthDateOrOrganisationNumber,
        getSortValue: (row) => row.birthDateOrOrganisationNumber,
      },
      ...form.shareTypes.map<TableColumn<ShareholderDTO>>((shareType) => ({
        name: `sharetypeName-${shareType.shareTypeCode}`,
        label: t("documents.attendingShareholders.numberShares", {
          shareTypeName: form.shareTypes.length > 1 ? findShareTypeName(shareType, lang) : "",
        }),
        align: "left",
        getValue: (row: ShareholderDTO) => (
          <Fragment>
            {formatNumber(form.getNumberOfSharesForShareholder(row, shareType))}
            &nbsp;(
            {formatPercent(form.getOwnershipPercentage(row, shareType))})
          </Fragment>
        ),
        getSortValue: (row: ShareholderDTO) => form.getNumberOfSharesForShareholder(row, shareType),
        getFooterValue: () => (
          <div>
            {formatNumber(form.numberOfPresentShares[shareType.sharetypeId] || 0)}
            &nbsp;({formatPercent(form.calculatePresenceForSharetype(shareType))})
          </div>
        ),
      })),
      {
        name: "representedBy",
        label: t("documents.attendingShareholders.representedBy"),
        align: "left",
        getValue: (row) => (
          <Autocomplete
            disabled={form.isLocked}
            placeholder={t("documents.attendingShareholders.representative")}
            items={form.representShareholdersOptions(row.id)}
            value={form.representShareholders.get(row.id) || null}
            onSelect={(item) => form.setRepresentShareholderValue(row.id, item)}
            onClear={() => form.setRepresentShareholderValue(row.id, null)}
            filterFn={form.filterRepresentShareholdersOptions}
            idFn={(item) => String(item.companyRoleId ?? "")}
            labelFn={(item) => item.name}
          />
        ),
      },
    ];

    return (
      <>
        {form.isAttendingOpen && (
          <Dialog
            title={t("documents.attendingShareholders.whoPresentHeader")}
            submitLabel={t("save")}
            isSubmitting={form.isLocked || form.isUpdatingParticipationData}
            onSubmit={save}
            onCancel={() => form.hideAttendingDialog()}
            fullscreen
          >
            {form.isUpdatingParticipationData && <LoadingIndicator />}
            {!form.isUpdatingParticipationData && (
              <div onSubmit={save}>
                <VBox gap={"lg"}>
                  <Checkbox
                    label={t("documents.attendingShareholders.toggleAll")}
                    onChange={(e) => form.toggleAllShareholdersPresent(e.target.checked)}
                    checked={form.isAllShareholdersPresent}
                    disabled={form.isLocked}
                  />
                  <DataTable
                    variant={"LARGE"}
                    tableColumns={columns}
                    tableData={form.availableParticipants}
                    getId={(row) => row.id}
                    initialSortBy={"participatingShareholders"}
                    initialSortDirection={"asc"}
                    withFooterRow
                  />
                </VBox>
              </div>
            )}
          </Dialog>
        )}
      </>
    );
  },
);

const ShareholderVotingDialog: FC<{ form: GeneralMeetingMinutesForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  return (
    <>
      {form.isVotingOpen && (
        <Dialog
          title={t("documents.generalMeetingVoting.registerVotes")}
          submitLabel={t("save")}
          isSubmitting={form.isSavingGeneralMeetingVotes}
          onSubmit={form.saveVotes}
          onCancel={form.hideVotingDialog}
          maxWidth={"md"}
        >
          <div onSubmit={form.saveVotes}>
            {form.isSaveGeneralMeetingError && (
              <Alert type={"error"} message={t("documents.generalMeetingVoting.saveError")} />
            )}
            <Table variant={"IN_CARD_DENSE"}>
              <thead>
                <tr>
                  <th>{t("documents.attendingShareholders.shareholder")}</th>
                  <th style={{ verticalAlign: "top" }}>{t("vote")}</th>
                </tr>
              </thead>
              <tbody>
                {form.currentVotes.map((shareholderVote) => (
                  <tr key={shareholderVote.shareholderId}>
                    <td>{form.shareholderNamesById[shareholderVote.shareholderId]}</td>
                    <td>
                      <RadioButtonGroup<IndividualVote>
                        name={`{${shareholderVote.shareholderId}-individualVote`}
                        horizontal
                        options={["for", "against", "blank"]}
                        idFn={identity}
                        labelFn={(value) =>
                          value === "for"
                            ? t("documents.generalMeetingVoting.voteFor")
                            : value === "against"
                              ? t("documents.generalMeetingVoting.voteAgainst")
                              : value === "blank"
                                ? t("documents.generalMeetingVoting.voteBlank")
                                : ""
                        }
                        onChange={(_, option) =>
                          form.setIndividualVote(shareholderVote.shareholderId, option)
                        }
                        selected={shareholderVote.vote}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Dialog>
      )}
    </>
  );
});

const MeetingForm: FC<{ form: GeneralMeetingMinutesForm }> = observer(({ form }) => {
  return (
    <VBox>
      <RadioFieldQuestion
        field={form.meetingForm}
        fieldName={"meetingForm"}
        question={{ key: "documents.meetingform" }}
        row
        isLocked={form.isLocked}
      />
      {isCirculationInvalid(
        form.meetingForm.value,
        form.context.generalMeetingFields.isSimplifiedGeneralMeeting,
      ) && <Alert type={"warning"} message={{ key: "general-meeting.meetingform.info" }} />}
    </VBox>
  );
});

const GeneralMeetingType: FC<{ form: GeneralMeetingMinutesForm }> = observer(({ form }) => {
  return (
    <VBox gap={"md"}>
      <RadioFieldQuestion
        field={form.generalMeetingType}
        fieldName={"generalMeetingType"}
        question={{ key: "documents.generalMeetingType" }}
        row
        isLocked={form.isLocked}
      />
      {when(annualAccountWarning(form.context), (props) => (
        <Alert {...props} />
      ))}
    </VBox>
  );
});

const RepresentedShareholdersOutput: FC<{ form: GeneralMeetingMinutesForm }> = observer(
  ({ form }) => {
    const { t, lang } = useTranslation();
    return (
      <VBox gap={"md"}>
        {form.repairTextDeadlineGeneralMeeting && (
          <Box>
            <Alert
              type="error"
              message={t("documents.generalMeeting.shorterDeadlineNotAllShareholdersPresent")}
            />
          </Box>
        )}
        {form.shareholderRepresentation.length > 0 && (
          <table>
            <tbody>
              <tr>
                <td>
                  <b>{t("documents.attendingShareholders.represented")}</b>
                </td>
              </tr>
              {form.shareholderRepresentation.map((representation) => {
                const shareType = form.getShareType(representation);
                return (
                  <tr key={representation.shareTypeId}>
                    <td>
                      {t(`documents.attendingShareholders.manyClasses`, {
                        shareNumber: { type: "number", value: representation.sharesParticipating },
                        totalShares: { type: "number", value: representation.totalShares },
                        shareClass: shareType ? findShareTypeName(shareType, lang) : "",
                        percentage: representation.percentage,
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <Button
          variant={"primary"}
          label={t("documents.attendingShareholders.whoPresent")}
          disabled={form.isLocked}
          onClick={() => form.showAttendingDialog()}
        />
      </VBox>
    );
  },
);

const Votes: FC<VotesProps> = observer(
  ({ voting, data, onChange, onLinkClick, isProjectLocked, isElectronicGeneralMeeting }) => {
    return (
      <VotingOutcomeSettings
        model={voting}
        data={data}
        isDisabled={isProjectLocked ?? false}
        isElectronicGeneralMeeting={isElectronicGeneralMeeting}
        onChange={onChange}
        onLinkClick={onLinkClick}
      />
    );
  },
);

const NewSignerDialog = observer(({ form }: { form: NewSignerForm }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("decisions.newSigner.title")}
      onCancel={form.cancel}
      onSubmit={form.submit}
      submitLabel={t("save")}
      isSubmitting={form.isSaving}
    >
      <VBox>
        <MTextInput label={t("decisions.newSigner.name")} model={form.name} />
        <MTextInput label={t("decisions.newSigner.email")} model={form.email} />
      </VBox>
    </Dialog>
  );
});

export const GeneralMeetingAgendaItemDetails = observer(function GeneralMeetingAgendaItemDetails({
  form,
  decisionId,
  renderDecisionForm,
  renderDecisionAttachment,
}: {
  form: GeneralMeetingMinutesForm;
  decisionId: string;
  renderDecisionForm: (decisionId: string) => ReactNode;
  renderDecisionAttachment: (decisionId: string) => ReactNode;
}) {
  const { t } = useTranslation();

  const decision = form.currentDecisions.find((d) => d.decisionId === decisionId);
  if (!decision) {
    return null;
  }

  const editor = form.decisionTextModels.find((d) => d.decisionId === decisionId);
  const votingForDecision = form.votes.getVotingForDecision(decisionId);
  return (
    <VBox gap={"md"}>
      {form.isDecisionMatter(decisionId) && (
        <VotingTypeSettings
          defaultVotingType={decision.defaultGeneralMeetingVotingType}
          isApproved={decision.isApprovedByGeneralMeeting}
          isDisabled={form.isLocked}
          isVotesRegistered={votingForDecision.isVotesRegistered}
          voting={votingForDecision}
        />
      )}
      {renderDecisionForm(decisionId)}
      {decision.type !== DecisionType.freetext && editor && (
        <DecisionTextsView form={editor} isLocked={form.isLocked} />
      )}
      {renderDecisionAttachment(decisionId)}
      <VBox>
        <strong>{t("dbot.voting")}</strong>
        {form.isDecisionMatter(decisionId) ? (
          <Votes
            voting={form.votes.getVotingForDecision(decisionId)}
            onChange={(voting) => form.setVotingForDecision(decisionId, voting)}
            onLinkClick={() => form.showDecisionVotingDialog(decisionId)}
            isProjectLocked={form.isLocked}
            isElectronicGeneralMeeting={form.isElectronicGeneralMeeting}
          />
        ) : (
          <div className={"text-sm"}>{t("freetext.informationMatter.description")}</div>
        )}
      </VBox>
    </VBox>
  );
});

export const GeneralMeetingDetails = observer(function GeneralMeetingDetails({
  form,
}: {
  form: GeneralMeetingMinutesForm;
}) {
  const { t } = useTranslation();

  return (
    <VBox gap={"md"}>
      <DocumentLanguageSelector field={form.documentLanguage} isLocked={form.isLocked} />
      <TimeOfGeneralMeeting
        dateField={form.context.generalMeetingFields.dateGeneralMeeting}
        timeField={form.context.generalMeetingFields.timeGeneralMeeting}
        isLocked={form.isLocked}
      />
      <GeneralMeetingType form={form} />
      <YesNoQuestion
        question={t("documents.generalMeeting.isSimplifiedGeneralMeeting")}
        field={form.context.generalMeetingFields.isSimplifiedGeneralMeeting}
        onChange={(e) => form.setSimplifiedGeneralMeeting(e)}
        fieldName={"GMSimplified"}
        isLocked={form.isLocked}
      />
      <MeetingForm form={form} />
      <PlaceOfGeneralMeeting
        meetingForm={form.meetingForm}
        meetingPlace={form.context.generalMeetingFields.meetingPlace}
        inlineAddressValue={form.context.generalMeetingFields.inlineAddressValue}
        isLocked={form.isLocked}
      />
      <LocalizedTextEditor model={form.additionalDetailsTextModel} />
    </VBox>
  );
});

export const GeneralMeetingParticipation = observer(function GeneralMeetingParticipation({
  form,
}: {
  form: GeneralMeetingMinutesForm;
}) {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      {form.numberOfShareholders > 0 ? (
        <RepresentedShareholdersOutput form={form} />
      ) : (
        <TextQuestion
          question={t("documents.generalMeeting.soleShareholderQuestion")}
          inputLabel={t("documents.generalMeeting.soleShareholderPlaceholder")}
          field={form.companySoleStockholder}
          isLocked={form.isLocked}
        />
      )}
      <Checkbox
        label={t(
          "documents.generalMeetingMinutes.generateShareholderRepresentationAsAttachmentQuestion",
        )}
        checked={form.context.generalMeetingFields.includeListOfParticipantsInMinutes.value}
        onChange={(e) => {
          form.context.generalMeetingFields.includeListOfParticipantsInMinutes.setValue(
            e.target.checked,
          );
        }}
        disabled={
          form.isLocked ||
          form.shareholderRepresentation.length === 0 ||
          (form.context.generalMeetingFields.includeListOfParticipantsInMinutes.value &&
            form.context.generalMeetingFields.includeListOfParticipantsInMinutes.isRequiredTrue)
        }
      />
      {form.context.generalMeetingFields.includeListOfParticipantsInMinutes.error && (
        <Alert
          type="error"
          message={
            <>{t(form.context.generalMeetingFields.includeListOfParticipantsInMinutes.error)}</>
          }
        />
      )}
    </VBox>
  );
});

export const GeneralMeetingChairAndSigner = observer(function GeneralMeetingChairAndSigner({
  form,
}: {
  form: GeneralMeetingMinutesForm;
}) {
  return (
    <VBox gap={"md"}>
      <GeneralMeetingMinutesSigners
        context={form.context}
        openNewSigner={form.openNewSigner}
        newSigner={form.newSigner}
        showInfo
      />
      <Votes
        voting={form.votes.meetChair}
        onChange={form.setMeetChairVoting}
        onLinkClick={form.showMeetingChairVoting}
        isProjectLocked={form.isLocked}
        isElectronicGeneralMeeting={form.isElectronicGeneralMeeting}
      />
    </VBox>
  );
});

export const GeneralMeetingMinutesSigners = observer(function GeneralMeetingSigners({
  context,
  newSigner,
  openNewSigner,
  showInfo,
}: {
  context: ProjectContext;
  newSigner: NewSignerForm | null;
  openNewSigner: (
    option: NewSigner,
    signer?: keyof SelectedSigners,
    additionalCosignerIndex?: number,
  ) => void;
  showInfo?: boolean;
}) {
  const { t } = useTranslation();

  const { meetingChair, coSigner, additionalCoSigners, meetingForm, isSimplifiedGeneralMeeting } =
    context.generalMeetingFields;
  const roles = context.initialState.roles;
  const cosignerOptions = sortBy(
    roles
      .filter(
        (r) =>
          r.companyroleId !== meetingChair.value?.companyroleId &&
          r.companyroleId !== coSigner.value?.companyroleId &&
          !additionalCoSigners.value.some((s) => s.id === r.companyroleId.toString()),
      )
      .map(mapRoleToCompanyRoleIdWithName),
    ["firstName", "lastName"],
  );

  const isLocked = context.isLocked;
  return (
    <>
      <strong>
        {t(
          meetingForm.value === GeneralMeetingForm.circulation
            ? "choiceOfMeetingSigner"
            : !isSimplifiedGeneralMeeting.value
              ? "documents.generalMeeting.choiceOfMeetingLeaderAndCoSigner"
              : "documents.generalMeeting.choiceOfMeetingChair",
        )}
      </strong>
      <AutocompleteSigner
        disabled={isLocked}
        label={t(
          meetingForm.value === GeneralMeetingForm.circulation
            ? "documents.generalMeeting.meetingSigner"
            : "documents.generalMeeting.meetingChair",
        )}
        items={meetingChair.options}
        value={meetingChair.value}
        onSelect={(option) => {
          if (option && option.companyroleId !== undefined) {
            meetingChair.setValue(option);
          } else if (option !== undefined) {
            openNewSigner(option, "meetingChair");
          }
        }}
        onClear={() => meetingChair.setValue(undefined)}
      />
      {showInfo && isSimplifiedGeneralMeeting.value && (
        <Alert
          message={t("documents.generalMeeting.isSimplifiedGeneralMeeting.info")}
          type={"info"}
        />
      )}
      {!isSimplifiedGeneralMeeting.value && (
        <AutocompleteSigner
          disabled={isLocked}
          label={t("documents.generalMeeting.coSigner")}
          error={!coSigner.isValid}
          items={coSigner.options}
          value={coSigner.value}
          onSelect={(option) => {
            if (option && option.companyroleId !== undefined) {
              coSigner.setValue(option);
            } else if (option !== undefined) {
              openNewSigner(option, "coSigner");
            }
          }}
          onClear={() => coSigner.setValue(undefined)}
        />
      )}
      {additionalCoSigners.value.map((option, index) => (
        <Box key={option.id} alignItems={"end"}>
          <VBox flex={"auto"}>
            <AutocompleteSigner
              disabled={isLocked}
              label={t("documents.generalMeeting.coSigner")}
              items={compact([
                option.isSelected && { companyroleId: Number(option.id), name: option.name },
                ...cosignerOptions,
              ])}
              value={{ companyroleId: Number(option.id), name: option.name }}
              onSelect={(option) => {
                if (option && option.companyroleId !== undefined) {
                  additionalCoSigners.replace(index, {
                    id: option.companyroleId.toString(),
                    name: option.name,
                    isSelected: true,
                  });
                } else if (option !== undefined) {
                  openNewSigner(option, undefined, index);
                }
              }}
              onClear={() =>
                additionalCoSigners.replace(index, {
                  id: newUuid(),
                  name: "",
                  isSelected: false,
                })
              }
            />
          </VBox>
          <IconButton
            icon={"trash"}
            tooltip={t("delete")}
            onClick={() => additionalCoSigners.remove(option.id)}
          />
        </Box>
      ))}
      <Box>
        <LinkButton
          label={t("documents.generalMeeting.addCoSigner")}
          onClick={() =>
            additionalCoSigners.add({
              id: newUuid(),
              name: "",
              isSelected: false,
            })
          }
        />
      </Box>

      {newSigner && <NewSignerDialog form={newSigner} />}
    </>
  );
});

export const GeneralMeetingApprovalOfAgenda = observer(function GeneralMeetingApprovalOfAgenda({
  form,
}: {
  form: GeneralMeetingMinutesForm;
}) {
  const { t } = useTranslation();

  return (
    <VBox>
      <Votes
        voting={form.votes.approveNoticeAndAgenda}
        onChange={form.setApproveNoticeAndAgendaVoting}
        onLinkClick={form.showApproveNoticeAndAgendaVoting}
        isProjectLocked={form.isLocked}
        isElectronicGeneralMeeting={form.isElectronicGeneralMeeting}
      />
      {form.allShareholdersPresent &&
        isVoted(form.votes.approveNoticeAndAgenda) &&
        form.hasNotice &&
        form.isTooShortNotice && (
          <Box>
            <Alert
              type="info"
              message={t("documents.generalMeeting.shorterDeadlineNotAllShareholdersPresent")}
            />
          </Box>
        )}
    </VBox>
  );
});

export const GeneralMeetingMinutesDetailsPanel = observer(
  function GeneralMeetingMinutesDetailsPanel({
    panel,
    form,
    renderDecisionForm,
    renderDecisionAttachment,
  }: {
    panel: DetailsPanel;
    form: GeneralMeetingMinutesForm;
    renderDecisionForm: (decisionId: string) => ReactNode;
    renderDecisionAttachment: (decisionId: string) => ReactNode;
  }) {
    switch (panel.type) {
      case "decision":
        return (
          <GeneralMeetingAgendaItemDetails
            decisionId={panel.id}
            form={form}
            renderDecisionForm={renderDecisionForm}
            renderDecisionAttachment={renderDecisionAttachment}
          />
        );
      case "custom":
        switch (panel.id) {
          case GeneralMeetingCustomPanel.meetingDetails:
            return <GeneralMeetingDetails form={form} />;
          case GeneralMeetingCustomPanel.participation:
            return <GeneralMeetingParticipation form={form} />;
          case GeneralMeetingCustomPanel.chairAndSigner:
            return <GeneralMeetingChairAndSigner form={form} />;
          case GeneralMeetingCustomPanel.approvalOfAgenda:
            return <GeneralMeetingApprovalOfAgenda form={form} />;
        }
    }
    return null;
  },
);

export const GeneralMeetingMinutesView = observer(function GeneralMeetingMinutesView2({
  form,
}: {
  form: GeneralMeetingMinutesForm;
}) {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <VBox gap="xs">
        <DocumentSigningStatusLine context={form.context} dispatch={form.dispatch} />
        <DetailsSummaryList
          dispatch={form.dispatch}
          isLocked={form.isLocked}
          panels={getGeneralMeetingCustomPanels(form)}
        />
        <DetailsSummaryList
          dispatch={form.dispatch}
          isLocked={form.isLocked}
          panels={getGeneralMeetingCustomAgendaItems(form)}
        />
      </VBox>
      <DetailsSummaryList
        dispatch={form.dispatch}
        isLocked={form.isLocked}
        panels={getDecisionDetailsPanels(form)}
      />
      <AgendaActions
        isLocked={form.isLocked}
        context={form.context}
        dispatch={form.dispatch}
        uploadAttachments={prepUploadDocumentAttachmentsButton(form.context, form.dispatch)}
      />
      {when(prepDocumentAttachmentsPanel(form.context, form.dispatch), (props) => (
        <AttachmentsPanel {...props} />
      ))}
      {when(form.openSendForSigningDialogAction, (action) => (
        <>
          <Button
            variant={"primary"}
            label={t(action.label)}
            loading={isPending(form.context.documentSigners.get(form.documentId))}
            onClick={(e) => {
              if (e.shiftKey && !isProductionEnvironment()) {
                form.dispatch({
                  type: "dbot/autosign-document",
                  document: form.currentDocuments.find((d) => d.documentId === form.documentId)!,
                  label: { translated: "Autosign document" },
                });
              } else if (form.validate()) {
                form.dispatch(action);
              }
            }}
          />
          {form.validateMessageError.length > 0 &&
            form.validateMessageError.map((e) => (
              <Alert key={t(e.message)} type={"error"} message={t(e.message)} />
            ))}
        </>
      ))}
      {when(form.downloadSignedDocumentAction, (action) => (
        <Button
          variant={"secondary"}
          label={t(action.label)}
          onClick={() => form.dispatch(action)}
        />
      ))}
      <ReopenDocumentButton context={form.context} dispatch={form.dispatch} />
      <ShareholdersAttendingDialog form={form} />
      <ShareholderVotingDialog form={form} />
    </VBox>
  );
});
